/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useSentinelaJobs - Hook para Analytics de Envíos Masivos
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file useSentinelaJobs.ts
 * @version 1.0.0
 * @description Hook personalizado para obtener y procesar jobs de Sentinela
 * @created 2025-01-27
 * 
 * FEATURES:
 * - ✅ Fetch jobs desde Sentinela API
 * - ✅ Cálculo de métricas agregadas
 * - ✅ Procesamiento de datos para gráficos
 * - ✅ Distribución horaria real
 * - ✅ Tasas de éxito calculadas
 * - ✅ Cache y error handling
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getHatateToken } from '../helpers/utils';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface SendJob {
  jobId: string;
  userId: string;
  status: 'pending' | 'queued' | 'processing' | 'completed' | 'error';
  totalContacts: number;
  sentContacts: number;
  failedContacts?: number;
  filePath: string;
  createdAt: string;
  updatedAt?: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  progress: number;
}

export interface AnalyticsMetrics {
  // KPIs
  totalJobs: number;
  totalContactsSent: number;
  totalContactsFailed: number;
  successRate: number;
  averagePerDay: number;
  
  // Estados
  completedJobs: number;
  processingJobs: number;
  queuedJobs: number;
  errorJobs: number;
  
  // Timing
  averageDuration: number; // minutos
  averageSpeed: number; // mensajes por minuto
  
  // Distribución temporal
  jobsByDay: { date: string; count: number; sent: number }[];
  jobsByHour: number[][]; // [día][hora] para heatmap
}

export interface UseSentinelaJobsReturn {
  jobs: SendJob[];
  metrics: AnalyticsMetrics | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export const useSentinelaJobs = (userId: string, limit: number = 100): UseSentinelaJobsReturn => {
  const [jobs, setJobs] = useState<SendJob[]>([]);
  const [metrics, setMetrics] = useState<AnalyticsMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Calcular métricas agregadas desde los jobs
   */
  const calculateMetrics = useCallback((jobsList: SendJob[]): AnalyticsMetrics => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Filtrar jobs de últimos 30 días
    const recentJobs = jobsList.filter(job => {
      const jobDate = new Date(job.createdAt);
      return jobDate >= thirtyDaysAgo;
    });

    // KPIs básicos
    const totalJobs = recentJobs.length;
    const completedJobs = recentJobs.filter(j => j.status === 'completed').length;
    const processingJobs = recentJobs.filter(j => j.status === 'processing').length;
    const queuedJobs = recentJobs.filter(j => j.status === 'queued').length;
    const errorJobs = recentJobs.filter(j => j.status === 'error').length;

    const totalContactsSent = recentJobs.reduce((sum, job) => sum + job.sentContacts, 0);
    const totalContactsFailed = recentJobs.reduce((sum, job) => sum + (job.failedContacts || 0), 0);
    const totalContactsAttempted = totalContactsSent + totalContactsFailed;

    const successRate = totalContactsAttempted > 0 
      ? (totalContactsSent / totalContactsAttempted) * 100 
      : 0;

    // Promedio por día
    const averagePerDay = totalJobs > 0 ? totalContactsSent / 30 : 0;

    // Duración y velocidad promedio (solo jobs completados)
    let averageDuration = 0;
    let averageSpeed = 0;

    const completedJobsWithTiming = recentJobs.filter(job => 
      job.status === 'completed' && job.startedAt && job.completedAt
    );

    if (completedJobsWithTiming.length > 0) {
      const durations: number[] = [];
      const speeds: number[] = [];

      completedJobsWithTiming.forEach(job => {
        const start = new Date(job.startedAt!).getTime();
        const end = new Date(job.completedAt!).getTime();
        const durationMs = end - start;
        const durationMin = durationMs / 1000 / 60;

        durations.push(durationMin);

        if (durationMin > 0) {
          const speed = job.sentContacts / durationMin;
          speeds.push(speed);
        }
      });

      averageDuration = durations.reduce((a, b) => a + b, 0) / durations.length;
      averageSpeed = speeds.length > 0 
        ? speeds.reduce((a, b) => a + b, 0) / speeds.length 
        : 0;
    }

    // Jobs por día (últimos 30 días)
    const jobsByDayMap = new Map<string, { count: number; sent: number }>();

    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dateKey = date.toISOString().split('T')[0];
      jobsByDayMap.set(dateKey, { count: 0, sent: 0 });
    }

    recentJobs.forEach(job => {
      const dateKey = job.createdAt.split('T')[0];
      const existing = jobsByDayMap.get(dateKey);
      if (existing) {
        existing.count += 1;
        existing.sent += job.sentContacts;
      }
    });

    const jobsByDay = Array.from(jobsByDayMap.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Heatmap: distribución por hora y día de la semana
    // jobsByHour[dia][hora] donde dia: 0=Dom, 1=Lun, ..., 6=Sáb
    const jobsByHour: number[][] = Array(7).fill(0).map(() => Array(24).fill(0));

    recentJobs.forEach(job => {
      const date = new Date(job.createdAt);
      const dayOfWeek = date.getDay(); // 0=Dom, 6=Sáb
      const hour = date.getHours();
      jobsByHour[dayOfWeek][hour] += job.sentContacts;
    });

    return {
      totalJobs,
      totalContactsSent,
      totalContactsFailed,
      successRate,
      averagePerDay,
      completedJobs,
      processingJobs,
      queuedJobs,
      errorJobs,
      averageDuration,
      averageSpeed,
      jobsByDay,
      jobsByHour
    };
  }, []);

  /**
   * Fetch jobs desde Sentinela API
   */
  const fetchJobs = useCallback(async () => {
    if (!userId) {
      setError('User ID no disponible');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = getHatateToken();
      if (!token) {
        throw new Error('Token de autenticación no disponible');
      }

      const response = await axios.get(
        `https://backend.anubisai.net/api/sentinela/jobs?user_id=${userId}&limit=${limit}`,
        {
          headers: { 'Authorization': token },
          timeout: 10000
        }
      );

      if (response.data && response.data.jobs) {
        const jobsList = response.data.jobs as SendJob[];
        setJobs(jobsList);

        // Calcular métricas
        const calculatedMetrics = calculateMetrics(jobsList);
        setMetrics(calculatedMetrics);

        console.log('[useSentinelaJobs] Jobs loaded:', jobsList.length);
        console.log('[useSentinelaJobs] Metrics:', calculatedMetrics);
      } else {
        setJobs([]);
        setMetrics(null);
      }
    } catch (err: any) {
      console.error('[useSentinelaJobs] Error fetching jobs:', err);
      setError(err.message || 'Error al cargar jobs');
      setJobs([]);
      setMetrics(null);
    } finally {
      setIsLoading(false);
    }
  }, [userId, limit, calculateMetrics]);

  /**
   * Auto-fetch al montar
   */
  useEffect(() => {
    if (userId) {
      fetchJobs();
    }
  }, [userId, fetchJobs]);

  return {
    jobs,
    metrics,
    isLoading,
    error,
    refetch: fetchJobs
  };
};

export default useSentinelaJobs;